/**
 * Bounded ring buffer of X-Request-ID values observed by the client fetch
 * patch. Used by RequestIdBadge to surface the most recent ID in the UI for
 * bug reports, and by the toast helpers to attach the active ID to error
 * notifications.
 *
 * Module scope (not React state) so the patched fetch can write without a
 * component tree being available — the underlying request precedes any
 * provider mount on the first paint.
 *
 * Capacity is intentionally small: bug reports only ever care about the last
 * handful, and unbounded growth in a long-lived tab is its own hazard.
 */

const CAPACITY = 25;

export interface RequestIdEntry {
  /** UUID returned by ai-service in the X-Request-ID header. */
  rid: string;
  /** Best-effort URL the patched fetch saw (string-ified). */
  url: string;
  /** HTTP status code returned. */
  status: number;
  /** Date.now() at receive time. */
  ts: number;
}

const buffer: RequestIdEntry[] = [];
const listeners = new Set<() => void>();

export function pushRequestId(entry: RequestIdEntry): void {
  buffer.push(entry);
  if (buffer.length > CAPACITY) buffer.splice(0, buffer.length - CAPACITY);
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      // Listener errors must not stall the fetch path.
    }
  }
}

export function getRecentRequestIds(): RequestIdEntry[] {
  return buffer.slice().reverse();
}

export function latestRequestId(): RequestIdEntry | undefined {
  return buffer[buffer.length - 1];
}

/** Subscribe to buffer changes (returns an unsubscribe function). */
export function subscribeRequestIds(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Test-only: empty the buffer. Not exported for production callers. */
export function _resetRequestIdsForTest(): void {
  buffer.length = 0;
}
